import { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BaseModal } from '../ui/BaseModal';
import { FormInput } from '../ui/FormInput';
import { useCreateFolder } from '../../services/documents.service';

interface CreateFolderModalProps {
  companyId: string;
}

export interface CreateFolderModalRef {
  open: (parentId: string | null) => void;
}

interface FolderFormData {
  name: string;
}

export const CreateFolderModal = forwardRef<CreateFolderModalRef, CreateFolderModalProps>(
  ({ companyId }, ref) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [parentId, setParentId] = useState<string | null>(null);

    const {
      register,
      handleSubmit: handleFormSubmit,
      formState: { errors },
      reset,
    } = useForm<FolderFormData>({
      defaultValues: { name: '' },
    });

    const createFolderMutation = useCreateFolder(companyId);

    useImperativeHandle(ref, () => ({
      open: (parentId: string | null) => {
        setParentId(parentId);
        setIsOpen(true);
      },
    }));

    useEffect(() => {
      if (!isOpen) {
        reset();
      }
    }, [isOpen, reset]);

    const handleSubmit = (data: FolderFormData) => {
      createFolderMutation.mutate({ name: data.name, parentId }, { onSuccess: handleClose });
    };

    const handleClose = () => {
      reset();
      setIsOpen(false);
    };

    const isLoading = createFolderMutation.isPending;

    const footer = (
      <div className="flex gap-4">
        <button
          type="button"
          onClick={handleClose}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition"
          disabled={isLoading}
        >
          {t('documents.create_folder_modal.cancel')}
        </button>
        <button
          type="submit"
          form="create-folder-form"
          className="flex-1 px-4 py-2 bg-primary text-white rounded-md hover:bg-opacity-90 transition disabled:opacity-50"
          disabled={isLoading}
        >
          {t('documents.create_folder_modal.submit')}
        </button>
      </div>
    );

    return (
      <BaseModal
        isOpen={isOpen}
        onClose={handleClose}
        title={t('documents.create_folder_modal.title')}
        footer={footer}
        maxWidth="md"
      >
        <form
          id="create-folder-form"
          onSubmit={handleFormSubmit(handleSubmit)}
          className="space-y-6"
        >
          <FormInput
            label={t('documents.create_folder_modal.name.label')}
            type="text"
            placeholder={t('documents.create_folder_modal.name.placeholder')}
            error={errors.name?.message}
            required
            {...register('name', {
              required: t('documents.create_folder_modal.name.error.required'),
            })}
          />
        </form>
      </BaseModal>
    );
  },
);

CreateFolderModal.displayName = 'CreateFolderModal';
